<template>
  <div class="gmf-social-card gmf-context">

    <!-- Side menu -->
    <Teleport to="body" :disabled="isDesktop">
      <div
        class="gmf-social-card-navigation gmf-context"
        :class="{ 'gmf-social-card-navigation-detached': !isDesktop }"
        data-gmf
        @touchmove.stop.prevent
        @wheel.stop.prevent
      >
        <Transition name="gmf-fade-overlay">
          <div class="gmf-modal-overlay" v-if="expanded" @touchstart.stop
            @click.stop.prevent="expanded = false" @mousedown.stop @touchmove.stop.prevent></div>
        </Transition>
        <nav @mousedown.stop="expanded = false" @touchstart.stop class="gmf-social-card-expandable gmf-scrollbar"
          ref="expandable" :class="{ 'gmf-active': expanded }" @touchmove.stop @wheel.stop>
          <span class="gmf-meny-title">{{ t('social_feed.filter.title') }}</span>
          <template v-for="item in menuItems" :key="item.name">
            <button v-if="!item.isSpacer" class="gmf-social-card-navigation-item"
              :class="{ 'gmf-active': selectedItem?.value == item.value, 'gmf-btn-primary': item.isButton }"
              @mousedown.stop @click="clickItem(item)">
              <div class="gmf-social-card-navigation-text">{{ item.name }}</div>
            </button>
            <div v-else class="gmf-social-card-navigation-spacer" />
          </template>
        </nav>
      </div>
    </Teleport>

    <!-- Main column -->
    <div class="gmf-social-card-container">
      <!-- Header -->
      <div class="gmf-social-card-header" ref="header">
        <img v-if="logo" :src="logo">
      </div>

      <!-- Top banner -->
      <div class="gmf-social-card-avatar" :class="{ 'gmf-active': !showSmallAvatar }">
        <img ref="image" v-if="boardImage" @load="calculateHeight" style="width: 100%" :src="boardImage" />
      </div>

      <!-- Content container -->
      <div class="gmf-social-card-scrollable" ref="scroll" @scroll.passive="loadIfInViewport">
        <div class="gmf-social-card-content" :style="scrollHeight">
          <!-- Board header -->

          <div class="gmf-social-feed-content-header">
            <div class="gmf-social-feed-content-header-top">
              <div class="gmf-subheader">{{ selectedItem?.name }}</div>
              <template v-if="selectedBoard && followBoardsEnabled">
                <button v-if="!followedBoards[selectedBoard.id]" class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="toggleFollowBoard($event, selectedBoard)">
                  <Icon icon="follow" />
                  <span>{{ t('follow') }}</span>
                </button>
                <button v-else class="gmf-btn gmf-btn-sm gmf-btn-secondary" @click.stop.prevent="toggleFollowBoard($event, selectedBoard)">
                  <Icon icon="following" />
                  <span>{{ t('following') }}</span>
                </button>
              </template>
            </div>

            <div v-if="boardDescription" class="gmf-alert gmf-alert-info gmf-social-feed-description"
              :class="{ 'gmf-clickable': guidelinesEnabled }" @click="guidelinesModalOpen = true">
              {{ boardDescription }}
              <Icon v-if="guidelinesEnabled" icon="https://cdn.gamifiera.com/static/icons/v2/info_regular.svg" />
            </div>
          </div>

          <SocialBoardCards v-if="showBoardList" :boards="boards" @selectBoard="selectBoardById" />

          <!-- Posts in either grid vier or regular post view -->
          <template v-if="useGridView">
            <div class="gmf-social-card-grid" ref="grid">
              <CustomerImage v-for="(post, index2) in posts" :key="post.id + index2" :comment="post"
                :imageIndex="index2" :useInViewport="true" :intersectionObserverRoot="intersectionObserverRoot" />
            </div>
          </template>
          <template v-else>
            <Post :ref="'post-' + post.id" v-for="post in posts" :key="post.id" @loadReplies="loadReplies"
              @update:votes="v => post.votes = v" :post="post" :useInViewport="true" :intersectionObserverRoot="intersectionObserverRoot" />
          </template>

          <a v-show="hasMoreContent && !isLoading" class="gmf-btn gmf-btn-primary" ref="loadBtn"
            @click.prevent="loadNextPage" v-if="!isLoading">&nbsp;</a>
          <div v-if="!hasMoreContent && !isLoading" class="gmf-mt-5 gmf-center">{{ t('social_feed.no_more_content') }}
          </div>

        </div> <!-- End of content -->

        <!-- Footer -->
        <div class="gmf-social-feed-footer">
          <div @mousedown.stop="expanded = !expanded" @touchstart.stop
            class="gmf-btn gmf-btn-primary gmf-social-feed-footer-board" v-if="!isDesktop">
            <Icon icon="hamburger" />
          </div>
          <div @click="beginPost" class="gmf-btn gmf-btn-primary gmf-social-feed-footer-post">
            <Avatar disableOnlineIndicator disablePopup v-if="state.profile" :customer="state.profile" />
            <span style="flex-shrink: 0; line-height: 32px;">{{ t('social_feed.post_placeholder') }}</span>
          </div>
        </div>

      </div> <!-- End of scrollable -->

    </div>

    <!-- Guideline modal -->
    <Modal v-if="guidelinesEnabled" :stackModals="true" v-model="guidelinesModalOpen">
      <div style="max-width: 600px;">
        <h2 class="gmf-subheader" v-if="commentGuidelinesTitle" v-html="commentGuidelinesTitle"></h2>
        <p v-if="commentGuidelinesText" v-html="commentGuidelinesText"></p>
      </div>
    </Modal>

    <Modal :stackModals="true" v-model="postTypeSelectorOpen">
      <div class="gmf-comment-type-selector">
        <div class="gmf-modal-header">
          {{ t('comments.type_selector_title') }}
        </div>
        <div class="gmf-radio-list">
          <RadioButton v-for="t in availablePostTypes" v-model="selectedPostTypeKey" :value="t.key" :key="t.key"
            :label="t.label" />
        </div>
        <div style="text-align: center; margin-top: var(--gmf-p-3)">
          <div class="gmf-btn gmf-btn-primary" :disabled="!selectedPostType" @click="onPostTypeSelected">{{
            t('comments.type_selector_ok') }}</div>
        </div>
      </div>
    </Modal>

    <Modal :stackModals="true" v-model="boardSelectorOpen">
      <div class="gmf-board-selector-modal">
        <span class="gmf-header">{{ t('social_feed.filter.title') }}</span>
        <ul v-for="[name, group] of Object.entries(boardItems)" class="gmf-board-selector" :key="name">
          <span class="gmf-subheader">{{ t('social_feed.filter.' + name) }}</span>
          <li v-for="item in group" :key="item.name" class="gmf-board-selector-item"
            :class="{ 'gmf-active': selectedItem.value == item.value }" @click="selectBoard(item)">{{ item.name }}
          </li>
        </ul>
      </div>
    </Modal>
  </div>
</template>

<style>
/* ----------------------------------- */
/* 1. High-level / container classes   */
/* ----------------------------------- */

/* Wrapper classes for the Social Feed itself. */
.gmf-social-feed,
.gmf-modal-body.gmf-social-feed {
  padding: 0;
  width: 850px;
  max-width: 100%;
  margin: auto;
}

.gmf-context .gmf-modal-body.gmf-social-feed {
  background: none;
  overflow: hidden;
  height: 100%;
  max-height: 1200px;
}

/* The main social-card container. */
.gmf-social-card {
  height: 100%;
  max-width: 100%;
  display: flex;
  gap: var(--gmf-object-spacing);
  /* adds a gap for when not in modal */
  align-items: flex-start;
}

.gmf-modal-body .gmf-social-card {
  gap: 0;
  /* removes gap when in modal */
  padding: 0;
  overflow: hidden;
  align-items: stretch;
}

/* The container that holds the header, avatar, and scrollable feed. */
.gmf-social-card-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  position: relative;
}

/* The main content area that holds the posts in modal. */
.gmf-modal-body .gmf-social-card-container {
  background: var(--gmf-col-white);
}

/* ----------------------------------- */
/* 2. Header / navigation              */
/* ----------------------------------- */

/* (Optional) Title in the side menu. */
.gmf-meny-title {
  color: white !important;
  position: relative;
  z-index: 3;
  display: block;
  font-weight: bold !important;
  padding: var(--gmf-p-4);
}

/* The feed header, shown or hidden depending on context. */
.gmf-social-feed .gmf-social-card-header {
  padding: var(--gmf-p-3);
  height: 60px;
  display: none !important;
  /* hide the header by default */
}

.gmf-modal-body .gmf-social-card-header {
  display: block !important;
  /* show the header in modal */
}

.gmf-social-card-header {
  align-items: center;
  display: flex;
  padding: var(--gmf-p-4);
  z-index: 0;
}

.gmf-social-card-header>img {
  max-height: 100%;
  max-width: 200px;
}

/* ----------------------------------- */
/* 3. Side menu / Expandable nav       */
/* ----------------------------------- */

.gmf-social-card-navigation {
  position: sticky;
  top: 32px;
  border-radius: var(--gmf-rounding);
  overflow: hidden;
  overscroll-behavior-y: none;
}

.gmf-social-card-expandable {
  background: var(--gmf-socialfeed-menu-background-color);
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 250px;
  overflow: hidden;
  overflow-y: auto;
  min-height: 60vh;
  max-height: 70vh;
  user-select: none;

  overscroll-behavior-y: none;
}

.gmf-modal-body .gmf-social-card-navigation {
  border-radius: 0;
}

/* Make side menu fill modal on small screens */
.gmf-modal-body .gmf-social-card-expandable {
  min-height: 100%;
  max-height: 100%;
}

/* The side menu in "active" state. */
/*.gmf-social-feed .gmf-social-card-expandable.gmf-active .gmf-social-card-navigation-item.gmf-active {
  background: var(--gmf-socialfeed-menu-active-background-color);
}*/

/* Navigation items (links, filters, etc.) */
.gmf-social-card-navigation-item {
  align-items: center;
  display: flex;
  gap: 15px;
  padding: var(--gmf-p-4);
  cursor: pointer;
  transition: background 0.15s linear;
  border-radius: 0;

  /* button reset */
  border: none;
  background: none;
  margin: 0;
}

.gmf-social-card-navigation-item.gmf-active {
  /*background: var(--gmf-socialfeed-menu-active-background-color);
  color: var(--gmf-socialfeed-menu-active-text-color);*/
  font-weight: bold;
  border-left: 4px solid var(--gmf-sidemenu-emphasis-background-color);
}

.gmf-social-card-navigation-item .gmf-icon {
  width: 25px;
  height: 25px;
  flex-shrink: 0;
  filter: brightness(0) invert();
}

.gmf-social-card-navigation-item.gmf-btn-primary {
  margin-bottom: 2px;
}

.gmf-social-card-navigation-item.gmf-btn-primary:last-child {
  margin-bottom: 8px;
}

.gmf-social-card-navigation-spacer {
  flex: 1;
}

/* Make the side menu 200px wide on desktop. */
.gmf-social-feed .gmf-social-card-expandable {
  width: 200px;
}

/* Text color for items. */
.gmf-context .gmf-social-card-navigation-text {
  color: var(--gmf-socialfeed-menu-text-color);
}

/*
* Mobile/detached
*/

.gmf-social-card-navigation-detached {
  z-index: var(--gmf-modal-z-index);
  /* Above the button at 10 */
}

.gmf-social-card-navigation-detached .gmf-social-card-expandable {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: var(--gmf-modal-z-index);
  border-radius: 0;
  transform: translateX(-100%);
  will-change: transform;
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
  min-height: 100%;
  overflow-y: auto;

  opacity: 0;
  pointer-events: none;
}

.gmf-social-card-navigation-detached .gmf-social-card-expandable.gmf-active {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

/* ----------------------------------- */
/* 4. Avatar / top-banner styling      */
/* ----------------------------------- */

.gmf-social-feed .gmf-social-card-avatar {
  background: var(--gmf-col-lightgray);
  overflow: hidden;
  line-height: 0;
}

/* Absolutely position the top banner image. */
.gmf-social-card-avatar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-in-out;
  will-change: opacity;
}

.gmf-social-card-avatar.gmf-active {
  opacity: 1;
}

/* If you have a .gmf-avatar inside the banner, this adjusts it. */
.gmf-social-card-avatar .gmf-avatar {
  transform: translateX(-50%);
  margin-top: -30px;
  background: none;
  height: 448px;
  left: 50%;
}

.gmf-social-card-avatar .gmf-avatar-content {
  -webkit-user-drag: none;
  user-select: none;
  border-radius: 0;
}

/* ----------------------------------- */
/* 5. Scrollable content area          */
/* ----------------------------------- */

.gmf-social-feed .gmf-social-card-scrollable {
  position: relative;
}

.gmf-modal-body .gmf-social-card-scrollable {
  height: 100%;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: scroll;
  margin-top: 0px;
  border-radius: var(--gmf-rounding) var(--gmf-rounding) 0 0;
  overflow-x: hidden;
}

/* The main content area that holds the posts. */
.gmf-social-feed .gmf-social-card-content {
  container-type: inline-size;
  margin-top: 350px;
  position: relative;
  z-index: 1;
  border-radius: var(--gmf-rounding) var(--gmf-rounding) 0 0;

  /* Hacky approach to get background under footer + space for footer */
  padding-bottom: 128px;
  margin-bottom: -50px;
}

.gmf-modal-body .gmf-social-card-content {
  background: var(--gmf-col-lightgray);
}

/* Remove scrollbars from embedded containers. */
.gmf-social-card-content::-webkit-scrollbar,
.gmf-social-card-container::-webkit-scrollbar,
.gmf-social-card-scrollable::-webkit-scrollbar {
  display: none;
}


/* ----------------------------------- */
/* 6. Header area                      */
/* ----------------------------------- */

/* Sticky subheader at the top of content. */
.gmf-social-card-content .gmf-social-feed-content-header {
  background: inherit;
  z-index: 2;
  top: 0;
  display: flex;
  flex-direction: column;
  border-radius: var(--gmf-rounding) var(--gmf-rounding) 0 0;
}

.gmf-social-card-content .gmf-social-feed-content-header-top {
  padding: var(--gmf-p-4);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gmf-p-3);
}

.gmf-social-feed-content-header-top .gmf-subheader {
  margin: 0 !important;
}

.gmf-modal-body .gmf-social-card-content .gmf-social-feed-content-header {
  position: sticky;
}

.gmf-social-feed-content-header-top .gmf-btn {
  margin-bottom: -8px;
  margin-top: -8px;

  display: flex;
  gap: var(--gmf-p-2);
  align-items: center;
}

.gmf-social-feed-content-header-top .gmf-btn .gmf-icon {
  width: 16px;
  height: 16px;
}

/* header that is not inside a modal */
.gmf-social-feed-content-header:not(.gmf-modal-body .gmf-social-feed-content-header) {
  background: var(--gmf-col-info-bg);
  border-radius: var(--gmf-rounding);
  margin-bottom: var(--gmf-object-spacing);
  overflow: hidden;

  & .gmf-social-feed-content-header-top {
    padding-bottom: var(--gmf-p-3);
  }

  & .gmf-alert.gmf-social-feed-description {
    text-align: left;
    justify-content: flex-start;
    background: none;
    padding-bottom: var(--gmf-p-4);
    margin-bottom: 0;
  }
}

/* ----------------------------------- */
/* 7. Footer area                      */
/* ----------------------------------- */


/* The feed's sticky bottom footer (Post button, etc.). */
.gmf-social-feed-footer {
  position: sticky;
  display: flex;
  z-index: 1;
  width: 100%;
  bottom: var(--gmf-p-3);
  padding-inline: var(--gmf-p-4);
}

/* Give footer buttons a rounded look. */
.gmf-social-feed-footer>.gmf-btn {
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2) !important;
  border-radius: 32px;
}

.gmf-social-feed .gmf-social-feed-footer-post {
  display: flex;
  gap: var(--gmf-p-3);
  margin: auto;
  align-items: center;
}

/* The absolute positioning of the "Hamburger" in the footer. */
.gmf-social-feed .gmf-social-feed-footer-board {
  position: absolute;
  bottom: 0px;
  padding: 25px !important;
  line-height: 0;
}

.gmf-social-feed-footer-board .gmf-icon {
  position: absolute;
  transform: translate(-50%, -50%);
  --gmf-icon-color: var(--gmf-col-white);
}

/* Style the "Begin post" button. */
.gmf-btn.gmf-btn-primary.gmf-social-feed-footer-post {
  padding: 8px 16px !important;
  font-weight: bold !important;
}

.gmf-social-feed-footer-post .gmf-avatar {
  width: 32px;
  height: 32px;
  margin-left: calc(var(--gmf-p-3) * -1);
}

/* ----------------------------------- */
/* 8. Other element styling            */
/* ----------------------------------- */

/* Remove borders and shadows from .gmf-card inside the social feed. */
.gmf-social-feed .gmf-card {
  border: none !important;
  box-shadow: none;
}

.gmf-modal-body .gmf-social-feed .gmf-card {
  border-radius: 0;
}

.gmf-social-card .gmf-card.gmf-sp {
  margin-bottom: var(--gmf-p-5);
}

.gmf-social-card .gmf-card.gmf-sp>.gmf-sp-body {
  margin-bottom: var(--gmf-p-4);
}

/* Post images in modal feed. */
.gmf-modal-body .gmf-social-card .gmf-img-grid-wrap {
  margin-left: calc(var(--gmf-p-4)* -1);
  width: calc(var(--gmf-p-4)* 2 + 100%);
  border-radius: 0;
}

/* Alerts in the feed. */
.gmf-alert.gmf-social-feed-description {
  margin-top: 0;
  text-align: center;
  justify-content: center;
  align-items: center;
  display: flex;
  --gmf-icon-color: currentColor;
  gap: var(--gmf-p-2);
}

.gmf-social-feed-description .gmf-icon {
  margin-top: -1px;
}

/* ----------------------------------- */
/* 9. Board selector (modal)           */
/* ----------------------------------- */

.gmf-board-selector-modal {
  width: 260px;
}

.gmf-board-selector {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
}

.gmf-board-selector-item {
  display: flex;
  padding: var(--gmf-p-4);
  border-bottom: 1px solid var(--gmf-col-gray);
  transition: all 0.15s linear;
  cursor: pointer;
}

.gmf-board-selector-item:last-child {
  border-bottom: none;
}

.gmf-board-selector-item.gmf-active {
  font-weight: bold;
}

@media (hover: hover) {
  .gmf-board-selector-item:hover {
    background: var(--gmf-col-lightgray);
  }
}

/* ----------------------------------- */
/* 10. Comment type selector (modal)    */
/* ----------------------------------- */
.gmf-comment-type-selector {
  user-select: none;
}

.gmf-comment-type-selector .gmf-radio-list {
  margin-left: calc(0px - var(--gmf-modal-padding));
  margin-right: calc(0px - var(--gmf-modal-padding));
}

.gmf-context .gmf-modal-header {
  background: var(--gmf-brand-color);
  padding: var(--gmf-p-4);
  color: var(--gmf-col-white);
  font-size: var(--gmf-subheader-font-size);
  font-family: var(--gmf-subheader-font);
  font-weight: var(--gmf-subheader-font-weight);

  margin: calc(0px - var(--gmf-modal-padding));
  margin-bottom: var(--gmf-p-3);
  padding-right: 50px;
  border-radius: var(--gmf-modal-rounding) var(--gmf-modal-rounding) 0 0;
}

.gmf-context .gmf-radio-list {
  display: flex;
  flex-direction: column;
}

/* ----------------------------------- */
/* 11. Media queries                   */
/* ----------------------------------- */

/*@TODO use isDesktop and a class instead of media query*/
@media (max-width: 900px) {

  /* Remove the gap on smaller screens. */
  .gmf-social-card {
    gap: 0;
  }

  .gmf-social-feed .gmf-social-card-scrollable {
    border-radius: var(--gmf-rounding);
  }
}

/* Container-based media query for the grid. */
@container (max-width: 500px) {
  .gmf-social-card-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .gmf-social-card-grid .gmf-customer-image:nth-child(24n - 10),
  .gmf-social-card-grid .gmf-customer-image:nth-child(24n + 1) {
    grid-column: initial !important;
    grid-row: initial !important;
  }

  .gmf-social-card-grid .gmf-customer-image:nth-child(5n) {
    grid-column: span 2 !important;
    grid-row: span 2 !important;
  }
}

/* ----------------------------------- */
/* 12. Grid-based display of posts     */
/* ----------------------------------- */

.gmf-social-card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  gap: var(--gmf-p-2);
}

.gmf-modal-body .gmf-social-card-grid {
  padding: var(--gmf-p-2);
}

.gmf-social-card-grid .gmf-customer-image:nth-child(24n - 10),
.gmf-social-card-grid .gmf-customer-image:nth-child(24n + 1) {
  grid-column: span 2;
  grid-row: span 2;
}
</style>

<script>
import { defineAsyncComponent } from 'vue';

const Post = defineAsyncComponent(() => {
  return import(/* webpackChunkName: "stage-2" */'./Post');
});

import callApi from '@/utils/callApi';
import Avatar from '@/components/Avatar.vue';
import state from '@/state';
import md from '@/utils/md';
import requestLogin from '@/utils/requestLogin';
import elSize from '@/utils/mixins/elSize';
import tooltip from '@/utils/tooltip';
import Icon from '@/components/Icon';
import Modal from '@/components/Modal';
import CustomerImage from '@/components/CustomerImage';
import RadioButton from '@/components/input/RadioButton';
import openCommentFormV2 from '@/components/CommentFormV2';
import SocialBoardCards from './SocialBoardCards.vue';

function getIdMap(posts, idMap = {}) {
  for (const post of posts) {
    idMap[post.id] = post;
    if (post.replies) {
      getIdMap(post.replies, idMap);
    }
  }
  return idMap;
}

function getScrollParent(node) {
  if (node == null) {
    return null;
  }

  if (node.nodeName === 'HTML') {
    return node;
  }

  if (node.classList && node.classList.contains('gmf-modal-overlay')) {
    return node;
  } else {
    return getScrollParent(node.parentNode);
  }
}

export default {
  name: 'SocialFeed',
  components: {
    Post,
    Icon,
    Modal,
    Avatar,
    RadioButton,
    CustomerImage,
    SocialBoardCards,
  },
  inject: ['defaultFilter'],
  props: ['defaultBoardId', 'defaultCommentType'],
  data() {
    return {
      boardSelectorOpen: false,
      postTypeSelectorOpen: false,
      selectedPostTypeKey: null,

      state,

      isStickyNow: false,

      guidelinesModalOpen: false,

      posts: [],
      page: 1,
      isLoading: false,
      totalPostCount: 0,

      skip: 0,

      order: 'created.desc',

      boards: [],
      scrollParent: null,

      commentIdToScrollTo: null,

      selectedValue: 'all',

      include: 'author,votes,targets,linkedProducts,pollVotes',

      showSmallAvatar: false,
      expanded: false,
      marginTop: 0,

      followedBoards: {},
    };
  },
  watch: {
    selectedFilter() {
      // Clear posts immediately
      this.posts = [];
      this.isLoading = false;
      this.page = 1;
      this.skip = 0;
      this.totalPostCount = 0;
      
      // Load new content
      this.loadNextPage(true);

      this.$nextTick(() => {
        this.calculateHeight();
        if (this.$refs.scroll) {
          this.$refs.scroll.scrollTop = 0;
        }
        if (this.scrollParent) {
          this.scrollParent.scrollTop = 0;
        }
      });
    },
    elSize() {
      this.calculateHeight();
      /*if (this.$refs.footer && this.$refs.scroll) {
        //this.$refs.footer.style.width = this.$refs.scroll.clientWidth + 'px';
      }*/
    },
    selectedBoard() {
      if (this.selectedBoard) {
        this.updateBoardFollowStatus(this.selectedBoard);
      }
      this.$nextTick(() => {
        this.calculateHeight();
        if (this.$refs.scroll) {
          this.$refs.scroll.scrollTop = 0;
        }
        if (this.scrollParent) {
          this.scrollParent.scrollTop = 0;
        }
      });
    }
  },
  mixins: [elSize],
  computed: {
    showBoardList() {
      if (this.selectedItem?.value == 'all') {
        return this.state.merchantConfig?.widgets?.socialFeed?.startPage?.boardList?.enabled;
      }
      return false;
    },
    followBoardsEnabled() {
      return this.state.merchantConfig?.follow?.boards?.enabled;
    },
    selectedItem() {
      for (const item of this.menuItems) {
        if (item.value === this.selectedValue) {
          return item;
        }
      }
      return {
        value: 'all',
        icon: null,
        filter: null,
      };
    },
    useGridView() {
      return this.selectedItem?.value == 'all_posts_with_image';
    },
    boardDescription() {
      if (this.selectedCommentType) {
        return this.t('comments.social_feed_description.' + this.selectedCommentType) || this.t('social_feed.description');
      }
      if (this.selectedBoard && this.selectedBoard.description) {
        return this.selectedBoard.description;
      }
      if (this.selectedItem) {
        const phr = this.t('social_feed.tab_description.' + this.selectedItem.value);
        if (phr) {
          return phr;
        }

      }
      return this.t('social_feed.description');
    },
    boardImage() {
      if (this.selectedCommentType == 'marketplace') {
        const typeImage = this.state.merchantConfig.widgets?.socialFeed?.marketplace?.boardImage;
        if (typeImage) {
          return typeImage;
        }
      }
      return this.selectedBoard?.imageUrl || this.state.merchantConfig?.widgets?.defaultBoardImage;
    },
    selectedPostType() {
      for (const t of this.availablePostTypes) {
        if (t.key === this.selectedPostTypeKey) {
          return t;
        }
      }
      return null;
    },
    availablePostTypes() {
      const types = [];

      const cfg = this.state.merchantConfig.comments;
      if (cfg.marketplace && cfg.marketplace.enabled) {
        types.push({
          label: this.t('comments.tab_text.marketplace'),
          commentType: 'marketplace',
          targetId: null,
        });
      }

      if (cfg.social_post && cfg.social_post.enabled) {
        for (const board of this.boards) {
          types.push({
            label: board.name,
            commentType: 'social_post',
            targetId: board.id,
          });
        }
      }

      for (const t of types) {
        t.key = t.commentType + ':' + t.targetId;
      }

      return types;
    },
    selectedFilter() {
      return this.selectedItem.filter;
    },
    menuItems() {
      const items = [
        {
          value: 'all',
          name: this.t('social_feed.filter.all_posts'),
          icon: null,
          filter: null,
        },
        {
          value: 'all_posts_with_image',
          name: this.t('social_feed.filter.all_posts_with_image'),
          icon: null,
          filter: { has_images: 1 },
        }
      ];

      if (this.state.merchantConfig?.widgets?.socialFeed?.enablePollFilter) {
        items.push({
          value: 'polls',
          name: this.t('social_feed.filter.polls'),
          icon: null,
          filter: { has_polls: 1 },
        });
      }

      for (const board of this.boards) {
        items.push({
          value: 'board-' + board.id,
          name: board.name,
          icon: null,
          filter: { board_id: board.id },
        });
      }

      for (const ct of this.enabledCommentTypes) {
        items.push({
          value: 'ct-' + ct.id,
          name: ct.name,
          icon: null,
          filter: { type: ct.id },
        });
      }

      return items;
    },
    boardItems() {
      const items = {};
      items['all'] = [
        {
          value: 'all',
          name: this.t('social_feed.filter.all_posts'),
        },
        {
          value: 'all_posts_with_image',
          name: this.t('social_feed.filter.all_posts_with_image'),
        }
      ];

      let boards = [];
      for (const board of this.boards) {
        boards.push({
          value: 'board-' + board.id,
          name: board.name,
        });
      }
      items['boards'] = boards;

      let commentTypes = [];
      for (const ct of this.enabledCommentTypes) {
        commentTypes.push({
          value: 'ct-' + ct.id,
          name: ct.name,
        });
      }
      items['products'] = commentTypes;

      return items;
    },
    scrollHeight() {
      this.selectedBoard; // trigger recalculation
      let exp = this.$refs.expandable;
      let height = exp?.clientHeight || 0;

      return {
          minHeight: height + 'px',
          marginTop: `max(calc(${this.marginTop}px - var(--gmf-rounding)), 0px)`
        };
    },
    logo() {
      return this.state.merchantConfig.widgets.logo || null;
    },
    popupProfileCardEnabled() {
      return !!this.state.merchantConfig?.popupProfileCard?.enabled;
    },
    translationsEnabled() {
      return this.state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    },
    boardMap() {
      const map = {};

      for (const board of this.boards) {
        map[board.id] = board;
      }

      return map;
    },
    enabledCommentTypes() {
      const result = [];
      const comments = this.state.merchantConfig.comments;
      for (const t in comments) {
        if (comments[t].enabled && t !== 'social_post') {
          result.push({
            id: t,
            name: state.t('comments.tab_text.' + t),
          });
        }
      }
      return result;
    },
    selectedCommentType() {
      if (this.selectedFilter && this.selectedFilter.type) {
        return this.selectedFilter.type;
      }
      return null;
    },
    selectedBoard() {
      if (this.selectedFilter && this.selectedFilter.board_id) {
        const board = this.boardMap[this.selectedFilter.board_id];
        if (board) {
          return board;
        }
      }
      return null;
    },
    query() {
      const q = {
        order: this.order,
        link: 'social_post',
      };
      if (this.selectedFilter) {
        Object.assign(q, this.selectedFilter);
      }
      return q;
    },
    idMap() {
      return getIdMap(this.posts);
    },
    hasMoreContent() {
      if (this.page == 1) {
        return true;
      }
      return this.posts.length < this.totalPostCount;
    },
    guidelinesEnabled() {
      if (!this.commentGuidelinesTitle && !this.commentGuidelinesText) {
        return false; // Content in the popup was explicitly empty.
      }

      return !!this.state.merchantConfig?.commentForm?.showGuidelinesSocialFeed;
    },
    commentGuidelinesTitle() {
      let text = md(state.t('comments.guidelines.social_feed.title'));
      if (text) {
        return text;
      }

      return false;
    },
    commentGuidelinesText() {
      let text = md(state.t('comments.guidelines.social_feed.text'));
      if (text) {
        return text;
      }

      return false;
    },
    profile() {
      return this.state.profile;
    },
    isDesktop() {
      return this.elSize.width >= 800;
    },
    allowedGuests() {
      let config = this.state.merchantConfig.comments;
      let allowSocialPost = config.social_post.enabled && config.social_post.allowGuests;
      let allowMarketplace = config.marketplace.enabled && config.marketplace.allowGuests;
      return allowSocialPost || allowMarketplace;
    },
    autoloadMargin() {
      if (this.useGridView) {
        return 1000;
      }

      return 300;
    },
  },
  mounted() {
    if (this.defaultFilter) {
      this.setFilter(this.defaultFilter);
    } else if (this.defaultBoardId) {
      this.setFilter({ board_id: this.defaultBoardId });
    } else if (this.defaultCommentType) {
      this.setFilter({ type: this.defaultCommentType });
    }

    this.loadNextPage();
    this.loadBoards();

    this.scrollParent = getScrollParent(this.$el);
    if (this.scrollParent.nodeName === 'HTML') {
      this.intersectionObserverRoot = null;
    } else {
      this.intersectionObserverRoot = this.$refs.scroll;
    }

    document.addEventListener('scroll', this.loadIfInViewport, { passive: true });
    if (this.scrollParent) {
      this.scrollParent.addEventListener('scroll', this.loadIfInViewport, { passive: true });
    }
    /*this.$nextTick(() => {
      if (this.$refs.footer && this.$refs.scroll) {
        // this.$refs.footer.style.width = this.$refs.scroll.clientWidth + 'px';
      }
    });*/

    this.$nextTick(() => {
      if (this.selectedValue == null) {
        this.selectedValue = this.menuItems[0];
      }
      this.calculateHeight();
    });
  },
  beforeUnmount() {
    document.removeEventListener('scroll', this.loadIfInViewport);
    if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.loadIfInViewport);
    }
  },
  methods: {
    async updateBoardFollowStatus(board) {
      if (!this.followBoardsEnabled) {
        return;
      }
      const res = await callApi('GET', `/v3/follow/social_board/${board.id}`);
      const data = await res.json();
      this.followedBoards[board.id] = data.following;
    },
    async toggleFollowBoard(ev, board) {
      if (!this.followBoardsEnabled) {
        return;
      }
      
      if (!this.profile) {
        tooltip.create({x: ev.pageX, y: ev.pageY}, this.t('error_text.log_in_to_follow'));
        return;
      }
      
      const shouldFollow = !this.followedBoards[board.id];
      const res = await callApi(shouldFollow ? 'POST' : 'DELETE', `/v3/follow/social_board/${board.id}`);
      if (res.status === 200) {
          this.followedBoards[board.id] = shouldFollow;
      } else {
          console.error('Failed to follow board ' + board.id);
      }
    },
    beginPost() {
      if (!this.state.profile && !this.allowedGuests) {
        this.login();
        return;
      }

      if (this.availablePostTypes.length > 1) {
        this.selectedPostTypeKey = null;
        this.postTypeSelectorOpen = true;
      } else {
        this.selectedPostTypeKey = this.availablePostTypes[0].key;
        this.$nextTick(() => {
          openCommentFormV2(this.selectedPostType.commentType, this.selectedPostType.targetId);
        });
      }
    },
    calculateHeight() {
      let headerHeight = this.$refs.header?.clientHeight || 0;

      let imgHeight = this.$refs.image?.clientHeight || 0;

      this.marginTop = Math.max(imgHeight - headerHeight, 0);
    },
    selectBoardById(id) {
      this.setFilter({ board_id: id });
    },
    selectBoard(item) {
      this.selectedValue = this.menuItems.find(o => o.value === item.value)?.value || null;
      this.boardSelectorOpen = false;
    },
    onPostTypeSelected() {
      this.postTypeSelectorOpen = false;
      if (this.selectedPostType) {
        openCommentFormV2(this.selectedPostType.commentType, this.selectedPostType.targetId);
      }
    },
    async ensureCommentLoaded(id) {
      if (id in this.idMap) {
        return;
      }
      const query = new URLSearchParams({
        'id:root': id,
        translate: this.translationsEnabled,
        include: this.include,
      });
      const res = await callApi('GET', state.commentSearchEndpoint + `?${query.toString()}`);
      const data = await res.json();
      if (data.comments.length > 0) {
        const comment = data.comments[0];
        if (!(comment.id in this.idMap)) {
          this.posts.unshift(comment);
        }
      }
    },
    setFilter(f) {
      const fJson = JSON.stringify(f);
      for (const t of this.menuItems) {
        if (JSON.stringify(t.filter) == fJson) {
          this.selectedValue = t.value;
          return;
        }
      }
      // @TODO we expect board filters to first fail because the board list has not been loaded yet
      // console.log('Could not apply filter', fJson);
      this.selectedValue = this.menuItems[0].value;

      this.desiredFilter = f;
    },
    clickItem(item) {
      if (this.expanded || this.isDesktop) {
        if (item.action) {
          item.action();
        } else {
          this.selectedValue = item.value;
        }
        this.expanded = false;
      } else {
        this.expanded = true;
      }
    },
    md,
    login() {
      requestLogin();
    },
    t: state.t,
    scrollToCommentId(cid) {
      // @TODO what we should really do here is to use an endpoint to find out where the comment is, then load that specific area and scroll.
      // For now, we will just hope that the comment will be loaded on the first page

      this.commentIdToScrollTo = cid;
    },
    async loadBoards() {
      const res = await callApi('GET', '/v1/social-boards');
      const data = await res.json();
      this.boards = data.boards;

      if (this.desiredFilter) {
        this.setFilter(this.desiredFilter);
        this.desiredFilter = null;
      }
    },
    async loadReplies(id) {
      if (this.isLoading) {
        return;
      }
      const queryAtStart = JSON.stringify(this.query);
      this.isLoading = true;

      const root = this.idMap[id];
      if (!root) {
        console.error('Failed to load replies: Parent comment not in view');
        return;
      }

      try {
        let skip = root.replies.length;
        if (skip && root.replies[0].replyCount === null) {
          skip = 0; // need to reload the replies
        }
        const query = Object.assign({}, this.query, {
          'skip': skip,
          'take': 3,
          'reply_count': 2,
          'reply_depth': 1,
          'root': root.id,
          'prioritize_pins': 'social_feed',
          'translate': this.translationsEnabled,
          'include': this.include,
        });
        const res = await callApi('GET', state.commentSearchEndpoint + '?' + new URLSearchParams(query).toString());
        // const res = await callApi('GET', state.commentSearchEndpoint + '?order=votes&skip=' + ((this.page - 1) * 10) + '&take=10&reply_count=10&reply_depth=10');

        if (queryAtStart != JSON.stringify(this.query)) {
          // Query has changed, throw result away
          this.isLoading = false;
          return;
        }

        const data = await res.json();

        for (const c of data.comments) {
          if (!(c.id in this.idMap)) {
            root.replies.push(c);
          } else {
            // Update existing comment, because it might have been a partial load
            Object.assign(this.idMap[c.id], c);
          }
        }
      } catch (e) {
        console.error('Reply load failed', e);
      }
      this.isLoading = false;
    },
    loadIfInViewport() {
      if (this.scrollParent) {
        if (this.scrollParent.scrollTop > 100) {
          this.isStickyNow = true;
        } else if (this.scrollParent.scrollTop < 4) {
          this.isStickyNow = false;
        }
      }
      if (!this.hasMoreContent) {
        return;
      }
      const btn = this.$refs.loadBtn;
      if (!btn) {
        return;
      }
      const r = btn.getBoundingClientRect();
      if (r.height <= 16) {
        return;
      }
      if (r.top - this.autoloadMargin <= (window.innerHeight || document.documentElement.clientHeight)) {
        this.loadNextPage();
      }
    },
    async loadNextPage(replaceContent = false) {
      if (replaceContent) {
        if (this.scrollParent) {
          this.scrollParent.scrollTop = 0;
        }
      }

      if (this.isLoading) {
        return;
      }
      const queryAtStart = JSON.stringify(this.query);
      this.isLoading = true;
      try {
        let take = (this.posts.length == 0 || replaceContent) ? 6 : 4;

        if (this.useGridView) {
          take = (this.posts.length == 0 || replaceContent) ? 16 : 12;
        }

        const query = Object.assign({}, this.query, {
          'skip': this.skip,
          'take': take,
          'reply_count': 2,
          'reply_depth': 2,
          'prioritize_pins': 'social_feed',
          'translate': this.translationsEnabled,
          'include': this.include,
          'has_content': 1,
          'order': state.merchantConfig?.widgets?.comments?.useSmartSort ? 'smart' : 'created.desc',
        });
        if (this.skip > 0) {
          query.no_count = 1; // Don't count the total number of comments
        }

        const res = await callApi('GET', state.commentSearchEndpoint + '?' + new URLSearchParams(query).toString());
        // const res = await callApi('GET', state.commentSearchEndpoint + '?order=votes&skip=' + ((this.page - 1) * 10) + '&take=10&reply_count=10&reply_depth=10');

        if (queryAtStart != JSON.stringify(this.query)) {
          // Query has changed, throw result away
          this.isLoading = false;
          return;
        }

        const data = await res.json();

        if (replaceContent) {
          this.posts = [];
        }

        if (data.totalCount !== null) {
          this.totalPostCount = data.totalCount;
        }
        this.posts.push(...data.comments.filter(c => !(c.id in this.idMap)));

        this.skip += take;

        if (this.commentIdToScrollTo) {
          await this.ensureCommentLoaded(this.commentIdToScrollTo);

          setTimeout(() => {
            let el = this.$refs['post-' + this.commentIdToScrollTo];
            if (Array.isArray(el)) {
              el = el[0];
            }
            if (el) {
              this.$refs.scroll.scrollTop = el.$el.offsetTop + this.marginTop - 40;
            }
            this.commentIdToScrollTo = null;
          }, 0);
        }

        this.page++;
      } catch (e) {
        console.error('Error loading page!', e);
        this.totalPostCount = 0; // Manually disabling autoload
      }
      this.isLoading = false;
    },
  },
};
</script>
